import { ApiError, type RoomState } from "./api";
import { MeetingMesh, type Remote } from "./mesh";

// The Office parent owns auth, disk and providers. The recorder receives no keys.
export function startOfficeRecorder() {
	document.body.textContent = "Office meeting recorder";
	let initialized = false;
	let stopping = false;
	let parentOrigin = "";
	let mesh: MeetingMesh | undefined;
	const pending = new Map<
		string,
		{
			resolve: (v: unknown) => void;
			reject: (e: Error) => void;
			timer: ReturnType<typeof setTimeout>;
		}
	>();
	function call(method: string, payload: unknown): Promise<unknown> {
		return new Promise((resolve, reject) => {
			const id = crypto.randomUUID();
			const timer = setTimeout(() => {
				pending.delete(id);
				reject(new Error("Office host unavailable"));
			}, 20_000);
			pending.set(id, { resolve, reject, timer });
			parent.postMessage(
				{ type: "meet-rpc", id, method, payload },
				parentOrigin,
			);
		});
	}
	const captures = new Map<string, { stop: () => void }>();
	let state: RoomState | null = null;
	let remotes: Remote[] = [];
	function refresh() {
		if (!state || stopping) return;
		const wanted = new Set<string>();
		for (const remote of remotes) {
			const peer = state.peers.find((p) => p.id === remote.id);
			if (!peer || peer.recorder) continue;
			for (const kind of ["audio", "camera", "screen"] as const) {
				if (kind !== "audio" && (!state.videoRecording || !peer[kind]))
					continue;
				const stream = remote[kind];
				if (
					!stream.getTracks().some((t) => t.readyState === "live" && !t.muted)
				)
					continue;
				const key = `${peer.id}:${kind}`;
				wanted.add(key);
				if (captures.has(key)) continue;
				let stopped = false;
				let timer: ReturnType<typeof setTimeout>;
				let recorder: MediaRecorder;
				function segment() {
					if (
						stopped ||
						stopping ||
						!stream.getTracks().some((t) => t.readyState === "live")
					)
						return;
					const start = Date.now();
					const chunks: Blob[] = [];
					const mime = (
						kind === "audio"
							? ["audio/webm;codecs=opus", "audio/mp4"]
							: ["video/webm;codecs=vp8", "video/mp4"]
					).find((m) => MediaRecorder.isTypeSupported(m));
					recorder = new MediaRecorder(stream, {
						...(mime ? { mimeType: mime } : {}),
						audioBitsPerSecond: 48000,
						videoBitsPerSecond: 650000,
					});
					recorder.ondataavailable = (e) => {
						if (e.data.size) chunks.push(e.data);
					};
					recorder.onstop = () => {
						const blob = new Blob(chunks, { type: recorder.mimeType });
						const chunkId = crypto.randomUUID();
						const duration = Date.now() - start;
						if (blob.size) {
							void blob.arrayBuffer().then(async (buffer) => {
								let binary = "";
								for (const byte of new Uint8Array(buffer))
									binary += String.fromCharCode(byte);
								const payload = {
									chunkId,
									meetingId: mesh!.id,
									speakerAddr: peer!.address,
									speakerName: peer!.name,
									kind,
									mimeType: blob.type,
									base64: btoa(binary),
									startOffsetMs: Math.max(0, start - state!.startedAt),
									durationMs: duration,
								};
								// A completed blob stays in memory until Office acknowledges durable storage.
								for (let attempt = 0; attempt < 20; attempt++) {
									try {
										await call("chunk", payload);
										return;
									} catch {
										await new Promise((r) => setTimeout(r, 3000));
									}
								}
								parent.postMessage(
									{
										type: "meet-recorder-error",
										message: "Office could not persist a captured interval.",
									},
									parentOrigin,
								);
							});
						}
						segment();
					};
					recorder.start();
					timer = setTimeout(() => {
						if (recorder.state !== "inactive") recorder.stop();
					}, 15_000);
				}
				segment();
				captures.set(key, {
					stop: () => {
						stopped = true;
						clearTimeout(timer);
						if (recorder.state !== "inactive") recorder.stop();
					},
				});
			}
		}
		for (const [key, c] of captures)
			if (!wanted.has(key)) {
				c.stop();
				captures.delete(key);
			}
	}
	window.addEventListener("message", (event) => {
		if (event.source !== parent) return;
		const data = event.data;
		if (data?.type === "meet-init" && !initialized) {
			initialized = true;
			parentOrigin = event.origin === "null" ? "*" : event.origin;
			mesh = new MeetingMesh(
				data.meetingId,
				{ address: data.address, token: "" },
				"Office recorder",
				true,
				async (body) => (await call("command", body)) as RoomState,
			);
			mesh.onState = (s) => {
				state = s;
				refresh();
			};
			mesh.onRemote = (r) => {
				remotes = r;
				refresh();
			};
			mesh.onEnded = () => {
				stopping = true;
				for (const c of captures.values()) c.stop();
				captures.clear();
				void call("ended", {});
			};
			void mesh
				.start()
				.catch((e) =>
					parent.postMessage(
						{ type: "meet-recorder-error", message: e.message },
						parentOrigin,
					),
				);
		} else if (data?.type === "meet-result") {
			const p = pending.get(data.id);
			if (!p) return;
			pending.delete(data.id);
			clearTimeout(p.timer);
			if (data.error) p.reject(new ApiError(data.error, data.status || 503));
			else p.resolve(data.result);
		} else if (data?.type === "meet-stop") {
			stopping = true;
			for (const c of captures.values()) c.stop();
			captures.clear();
			void mesh?.leave().catch(() => {});
		}
	});
	parent.postMessage({ type: "meet-ready" }, "*");
}
